import type { ProgramInputSchema } from "@brookbush/types";

import clsx from "clsx";
import { ChevronRightIcon } from "lucide-react";
import type * as React from "react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import type * as zod from "zod";
import { ZodObject, type ZodType } from "zod";

import ProgramFormDynamicInput from "./ProgramFormDynamicInput";
import { camelToTitle, unwrapSchema } from "~/utils/helpers";

interface ProgramFormSectionProps {
	label: string;
	formKey: string;
	schema: zod.ZodObject;
	level?: 2 | 3 | 4 | 5;
}

const hasAnyValue = (values: unknown): boolean => {
	if (values === null || values === undefined || values === "") return false;
	if (typeof values === "object" && !Array.isArray(values)) {
		return Object.values(values as Record<string, unknown>).some(hasAnyValue);
	}
	return Boolean(values);
};

const ProgramFormSection: React.FC<ProgramFormSectionProps> = ({
	schema,
	formKey,
	label,
	level = 2,
}) => {
	const { resetField, watch } = useFormContext<ProgramInputSchema>();
	const sectionValues = watch(formKey as any);
	const [isOpen, setIsOpen] = useState(() => hasAnyValue(sectionValues));

	useEffect(() => {
		if (!isOpen) {
			resetField(formKey as any);
		}
	}, [formKey, isOpen, resetField]);

	const dependsOn = schema.meta()?.dependsOn as
		| (keyof ProgramInputSchema)[]
		| undefined;

	const parentKeys = formKey.split(".").slice(0, -1);

	const dependsOnValues = dependsOn?.map((key) =>
		watch(
			(parentKeys.length > 0 ? [...parentKeys, key].join(".") : key) as any,
		),
	);

	const isDisabled = dependsOn
		? !dependsOnValues?.some((value) => value)
		: false;

	useEffect(() => {
		if (isDisabled) {
			setIsOpen(false);
		}
	}, [isDisabled]);

	const schemaEntries = Object.entries(schema.shape).map(([key, schema]) => {
		const { baseSchema, isOptional } = unwrapSchema(schema);

		return [key, baseSchema, isOptional];
	}) as [string, ZodType, boolean][];

	return (
		<div className={clsx("flex w-full flex-col gap-4")}>
			<label
				className={clsx(
					"flex w-fit cursor-pointer select-none items-center gap-2.5",
					{
						"pointer-events-none cursor-not-allowed opacity-90": isDisabled,
					},
				)}
			>
				<input
					disabled={isDisabled}
					type="checkbox"
					className={clsx(
						"checkbox rounded-md checked:bg-sky-500 checked:text-white dark:checked:bg-sky-600",
						level === 2 ? "laptop:size-5 size-4" : "laptop:size-4.5 size-4",
					)}
					checked={isOpen}
					onChange={({ target }) => setIsOpen(target.checked)}
				/>

				<div className="hover:opacity-75">
					<span
						className={clsx(
							"text-left font-semibold",
							level === 2
								? "laptop:text-xl text-md"
								: level === 3
									? "laptop:text-lg text-md"
									: "text-md",
						)}
					>
						{label}
					</span>
				</div>

				<ChevronRightIcon
					strokeWidth={2.4}
					className={clsx(
						"-ml-0.5 shrink-0 transition-transform duration-200 ease-in-out",
						{
							"rotate-90": isOpen,
							"size-5": level === 2,
							"size-4.5": level === 3,
							"size-4": level === 4,
							"size-3.5": level === 5,
						},
					)}
				/>
			</label>

			{isOpen && (
				<div className={"ml-2.5 flex flex-col gap-4 border-l pl-3"}>
					{schema.description && isOpen && (
						<p className="-mt-2 ml-2 w-full text-slate-500 dark:text-slate-300">
							{schema.description}
						</p>
					)}

					{schema.shape &&
						schemaEntries.map(([key, schema]) =>
							schema instanceof ZodObject ? (
								<ProgramFormSection
									key={key}
									formKey={`${formKey}.${key}`}
									level={(level + 1) as 2 | 3 | 4 | 5}
									schema={schema as ZodObject}
									label={schema.meta()?.title ?? camelToTitle(key.toString())}
								/>
							) : (
								<div key={key} className="ml-2 laptop:w-auto w-full">
									<ProgramFormDynamicInput
										key={key}
										formKey={`${formKey}.${key}`}
										schema={schema as ZodType}
										label={schema.meta()?.title ?? camelToTitle(key.toString())}
									/>
								</div>
							),
						)}
				</div>
			)}
		</div>
	);
};

export default ProgramFormSection;
